/**
 * Vuex store for the Master Lot Builder demo.
 *
 * Source-of-truth for master lots in the demo. Persists across page reloads
 * via `vuex-persist` as the `upstreamMasterLots` module
 * (registered in `store/index.js` modules list).
 *
 * Architecture: this module owns the persisted master-lot state. Source-lot
 * data (eligible refinery lots aggregated into picker units) comes from a
 * real admin-guarded backend endpoint — `GET /refinery/upstream-source-lots`.
 * Write paths (createMasterLot, updateMasterLotComposition, milestones,
 * cancel) go through `upstreamMasterLotMockApi` until live master-lot write
 * endpoints replace those calls and this module leaves `vuex-persist`.
 * Action shapes stay identical across that swap.
 *
 * IDs: every master lot uses its `masterLotCode` as its `id` (URL-safe).
 * Demo fixtures use `ML-2026-D###`; user-created lots use `ML-2026-####`.
 * The two number spaces don't collide because the user-create counter
 * derives from the highest `ML-{year}-{4-digit number}` extant in state.
 */

import request from '@/util/request'

import {buildUpstreamSourceLotDemoFixtures} from '@/api/refinery/upstreamSourceLotDemoFixtures'
import {
  createMasterLot as apiCreateMasterLot,
  updateMasterLotComposition as apiUpdateMasterLotComposition,
  confirmMasterLot as apiConfirmMasterLot,
  unconfirmMasterLot as apiUnconfirmMasterLot,
  updateMasterLotMetadata as apiUpdateMasterLotMetadata,
  fulfillSalesOrder as apiFulfillSalesOrder,
  advanceMilestone as apiAdvanceMilestone,
  setMilestoneEta as apiSetMilestoneEta,
  setMilestoneNote as apiSetMilestoneNote,
  cancelMasterLot as apiCancelMasterLot,
  recordSettlement as apiRecordSettlement,
  updateSettlement as apiUpdateSettlement,
  changeMasterLotContract as apiChangeMasterLotContract,
  seedDemoData as apiSeedDemoData,
} from '@/api/refinery/upstreamMasterLotMockApi'
import {
  upstreamMasterLotNetsuitePrototypeAdapter,
} from '@/api/refinery/upstreamMasterLotNetsuitePrototypeAdapter'
import {
  clearAllSettlementDocBlobs,
  confirmSettlementDoc as apiConfirmSettlementDoc,
  deleteSettlementDoc as apiDeleteSettlementDoc,
  presignSettlementDoc as apiPresignSettlementDoc,
  uploadMasterLotSettlementDocToS3,
} from '@/util/refinery/upstream/upstreamSettlementDocs'
import {
  buildSourceUnitAllocationMap,
  decorateSourceLotsWithReconciliation,
  decorateSourceLotsWithAllocation,
  DEFAULT_DEMO_NETSUITE_APP_URL,
  hydrateUpstreamSourceLotIdentity,
  normalizeUpstreamSourceLotPurity,
  reconciliationBuckets,
  reconciliationIdsForComplement,
  recoverSourceUnitReconciliations,
  upsertSourceUnitReconciliations,
} from '@/util/refinery/upstream/upstreamMasterLot'
import {MILESTONE_KINDS} from '@/util/refinery/upstream/constants'
const UPSTREAM_SOURCE_LOTS_ENDPOINT = '/refinery/upstream-source-lots'

const getters = {
  getMasterLots: (state) =>
    Object.values(state.byId).sort((a, b) => {
      const at = a?.createdAt || ''
      const bt = b?.createdAt || ''
      return bt.localeCompare(at)
    }),
  getMasterLotById: (state) => (id) => state.byId[id] || null,
  getUpstreamSourceLotsForMetals: (state) => (paidMetals) => {
    // null/undefined = global inventory (availability / reconciliation),
    // populated by fetchUpstreamSourceLotsForContract(null) into `__all__`.
    // An array is contract-scoped: empty → `__none__` (pays for nothing),
    // non-empty → sorted metals key. Empty must NOT fall through to `__all__`,
    // or a pays-nothing contract would surface global inventory.
    if (paidMetals == null) {
      return state.upstreamSourceLotsCacheByMetals.__all__ || null
    }
    return state.upstreamSourceLotsCacheByMetals[upstreamSourceLotsCacheKey(paidMetals)] || null
  },
  /**
   * Allocation map for the picker-side "this unit is already on another
   * non-cancelled master lot" guard. Map shape: `{[unitId]: masterLotCode}`.
   * Recomputed reactively on any `state.byId` mutation (upsert / cancel /
   * remove) so the picker UI updates with no extra plumbing. Cancelled lots
   * release their units.
   *
   * Client-synthesized until the listing endpoint emits
   * `allocatedMasterLotId` per picker row from `master_lot_components`.
   */
  getSourceUnitAllocationMap: (state) =>
    buildSourceUnitAllocationMap(Object.values(state.byId)),
  /**
   * Decorated picker-row source, composing the metal-keyed cache with the
   * reactive allocation map. The picker's `:lots` should bind to this getter
   * via a `computed`, NEVER through a `data` field — a stale copy in `data`
   * desyncs allocation decoration from live lot state.
   *
   * Returns `null` when the cache has no entry for the metals key (matching
   * `getUpstreamSourceLotsForMetals`) so callers can `|| []` defensively.
   *
   * Performance: parametric getter, intentionally NOT vuex-memoized — the
   * map / decoration runs once per consumer read. O(units) over single-digit
   * dozens of units in the demo is fine; the picker re-renders on the
   * underlying state changes anyway.
   */
  getDecoratedUpstreamSourceLotsForMetals: (state, getters) => (paidMetals) => {
    const cached = getters.getUpstreamSourceLotsForMetals(paidMetals)
    if (!cached) return null
    const allocated = decorateSourceLotsWithAllocation(cached, getters.getSourceUnitAllocationMap)
    return decorateSourceLotsWithReconciliation(allocated, state.reconciledSourceUnitsById)
  },
  isDemoSeeded: (state) => state.demoSeeded,
}

const actions = {
  async fetchMasterLots({state, dispatch}) {
    if (!state.demoSeeded && Object.keys(state.byId).length === 0) {
      await dispatch('bootstrapDemoIfEmpty')
    }
    return Object.values(state.byId)
  },

  async fetchMasterLotById({state, dispatch}, id) {
    if (!state.demoSeeded && Object.keys(state.byId).length === 0) {
      await dispatch('bootstrapDemoIfEmpty')
    }
    return state.byId[id] || null
  },

  /**
   * Fetch refinery source lots (decomposed into bar/house_pin/customer_pin
   * units). Hits the real admin-guarded backend endpoint; results are stored
   * per metals key for getters but always refetched (live inventory). Append
   * dedicated source-lot fixtures after live purity normalization for stable
   * prototype inventory alongside melt-complete-through-settled live rows.
   *
   * Call shapes:
   * - `null` contract → unfiltered global inventory into `__all__`
   *   (availability / reconciliation)
   * - contract with empty `paidMetals` → `__none__` empty (pays for nothing)
   * - contract with metals → filtered cache key
   *
   * Response shape: `util/request`'s response interceptor strips the axios
   * envelope (`return data`), so `request.get(...)` resolves directly to the
   * BE body — `{rows, total}` for this endpoint. NO `.data` wrapper.
   *
   * Purity normalization: backend emits per-bar purity in the raw DB form
   * (`DECIMAL(5,4)` fractions in [0, 1]) per the rest-of-app convention.
   * The master lot builder pipeline (math util, manifest summary, manual
   * entries, fixtures) standardizes on percent form (0–100), so we convert
   * once here at vuex ingress — mirroring the `purity * 100` translation
   * pattern in `views/refinery/RefineryLot.vue`.
   */
  async fetchUpstreamSourceLotsForContract({commit}, contract) {
    // Live inventory changes while the composer is open (new melt-complete
    // lots, etc.), so always refetch. State still holds the latest page for
    // getters; we intentionally do not forever-cache / short-circuit.
    //
    // null contract = global inventory (availability / reconciliation).
    // Contract with empty paidMetals = pays for nothing → no inventory.
    let key
    let params
    let fixturesArg
    if (contract == null) {
      key = '__all__'
      params = {}
      fixturesArg = null
    } else {
      const paidMetals = Array.isArray(contract.paidMetals) ? contract.paidMetals : []
      key = upstreamSourceLotsCacheKey(paidMetals)
      if (paidMetals.length === 0) {
        commit('SET_UPSTREAM_SOURCE_LOTS_FOR_METALS', {key, lots: []})
        return []
      }
      params = {
        paidMetals: paidMetals.map((metal) => String(metal).toLowerCase()).join(','),
      }
      fixturesArg = paidMetals
    }

    const body = await request.get(UPSTREAM_SOURCE_LOTS_ENDPOINT, {params})
    const rows = mergeUpstreamSourceLotsWithFixtures(
      (body?.rows || []).map(normalizeUpstreamSourceLotPurity),
      buildUpstreamSourceLotDemoFixtures(fixturesArg),
    ).map(hydrateUpstreamSourceLotIdentity)
    commit('SET_UPSTREAM_SOURCE_LOTS_FOR_METALS', {key, lots: rows})
    return rows
  },

  async createMasterLot({commit, state}, payload) {
    const nextCode = computeNextUserLotCode(state.byId)
    const lot = await apiCreateMasterLot(payload, {nextCode})
    commit('UPSERT_LOT', lot)
    return lot
  },

  async updateMasterLotComposition({commit, state}, {id, ...payload}) {
    const existing = state.byId[id]
    if (!existing) throw new Error(`master lot not found: ${id}`)
    const lot = await apiUpdateMasterLotComposition(existing, payload)
    commit('UPSERT_LOT', lot)
    return lot
  },

  async confirmMasterLot({commit, state, rootGetters}, masterLotId) {
    const existing = state.byId[masterLotId]
    if (!existing) throw new Error(`master lot not found: ${masterLotId}`)
    const lot = await apiConfirmMasterLot(existing, {
      netsuiteAppUrl: netsuiteAppUrlFrom(rootGetters),
    })
    commit('UPSERT_LOT', lot)
    return lot
  },

  async unconfirmMasterLot({commit, state}, masterLotId) {
    const existing = state.byId[masterLotId]
    if (!existing) throw new Error(`master lot not found: ${masterLotId}`)
    const lot = await apiUnconfirmMasterLot(existing)
    commit('UPSERT_LOT', lot)
    return lot
  },

  async updateMasterLotMetadata({commit, state}, {masterLotId, ...metadata}) {
    const existing = state.byId[masterLotId]
    if (!existing) throw new Error(`master lot not found: ${masterLotId}`)
    const lot = await apiUpdateMasterLotMetadata(existing, metadata)
    commit('UPSERT_LOT', lot)
    return lot
  },

  // We stamp the logged-in user as "recorded by" rather than asking who performed
  // the step: milestone authorship is for traceability, and the actor is implied
  // by the milestone kind (e.g. shipping is always us, receipt always the partner).
  async advanceMilestone({commit, state, rootGetters}, {masterLotId, kind, occurredAt, note}) {
    const existing = state.byId[masterLotId]
    if (!existing) throw new Error(`master lot not found: ${masterLotId}`)
    const fulfillment = kind === MILESTONE_KINDS.SHIPPED
      ? await apiFulfillSalesOrder(existing)
      : null
    const lotForAdvance = fulfillment
      ? {...existing, salesOrder: fulfillment.salesOrder}
      : existing
    const lot = await apiAdvanceMilestone(lotForAdvance, kind, {
      occurredAt,
      recordedByUserId: rootGetters.getUserId || null,
      recordedByName: rootGetters.getUsername || null,
      note,
    })
    commit('UPSERT_LOT', lot)
    return lot
  },

  async setMilestoneEta({commit, state}, {masterLotId, kind, expectedAt}) {
    const existing = state.byId[masterLotId]
    if (!existing) throw new Error(`master lot not found: ${masterLotId}`)
    const lot = await apiSetMilestoneEta(existing, kind, expectedAt)
    commit('UPSERT_LOT', lot)
    return lot
  },

  async setMilestoneNote({commit, state}, {masterLotId, kind, note}) {
    const existing = state.byId[masterLotId]
    if (!existing) throw new Error(`master lot not found: ${masterLotId}`)
    const lot = await apiSetMilestoneNote(existing, kind, note)
    commit('UPSERT_LOT', lot)
    return lot
  },

  async cancelMasterLot({commit, state}, masterLotId) {
    const existing = state.byId[masterLotId]
    if (!existing) throw new Error(`master lot not found: ${masterLotId}`)
    const lot = await apiCancelMasterLot(existing)
    commit('UPSERT_LOT', lot)
    return lot
  },

  /**
   * Re-base an in-flight master lot onto another (or re-snapshot the same)
   * active contract. Resolves the new contract from the sibling
   * `upstreamContracts` module's getter so the action stays a coordinator;
   * snapshot replacement, milestone union, and `contractHistory` audit append
   * happen at the mock-api boundary in one returned next-state object
   * (vuex commits once).
   *
   * Throws if the lot or the new contract id doesn't resolve. Status guard
   * lives at the api boundary: draft + confirmed only. Confirmed switches
   * return the lot to draft; shipped and later are immutable.
   */
  async changeMasterLotContract({commit, state, rootGetters}, {
    masterLotId,
    newContractId,
    notes,
    removeManifestRowKeys,
  }) {
    const existing = state.byId[masterLotId]
    if (!existing) throw new Error(`master lot not found: ${masterLotId}`)
    const newContract = rootGetters['upstreamContracts/getContractById'](newContractId)
    if (!newContract) throw new Error(`upstream refining contract not found: ${newContractId}`)
    const lot = await apiChangeMasterLotContract(existing, newContract, {notes, removeManifestRowKeys})
    commit('UPSERT_LOT', lot)
    return lot
  },

  // -------------------------------------------------------------------------
  // settlement actions
  //
  // recordSettlement   — atomic at the api boundary (settlement + settled
  //                      milestone + status flip in one returned object)
  // updateSettlement   — replace settlement payload on already-settled lot
  // uploadSettlementDoc — orchestrates presign → orchestrator PUT → confirm,
  //                      with cleanup-on-failure if confirm fails after
  //                      presign succeeded (matches PR #1183's `uploadPhoto`)
  // removeSettlementDoc — REST-name match: vuex action `removePhoto` mirror
  //                      from PR #1183, calls api `deleteSettlementDoc`.
  //
  // These actions touch the lot via `recordSettlement`/`updateSettlement` for
  // payload changes, OR via `uploadSettlementDoc`/`removeSettlementDoc` for
  // document-only changes. The dialog stitches them together: after upload
  // resolves, the dialog appends the returned doc record to its local array
  // and re-submits the full settlement on Save (which goes through
  // record/update). Documents in the persisted settlement payload are the
  // metadata only; bytes live in IndexedDB (via the api seam).
  // -------------------------------------------------------------------------
  async recordSettlement({commit, state}, {masterLotId, input}) {
    const existing = state.byId[masterLotId]
    if (!existing) throw new Error(`master lot not found: ${masterLotId}`)
    const persisted = await apiRecordSettlement(existing, input)
    const result = await upstreamMasterLotNetsuitePrototypeAdapter.simulateSettlementOutbound(persisted)
    return commitAdapterResult(commit, result)
  },

  async updateSettlement({commit, state}, {masterLotId, input}) {
    const existing = state.byId[masterLotId]
    if (!existing) throw new Error(`master lot not found: ${masterLotId}`)
    const persisted = await apiUpdateSettlement(existing, input)
    const result = await upstreamMasterLotNetsuitePrototypeAdapter.simulateSettlementOutbound(persisted)
    return commitAdapterResult(commit, result)
  },

  async simulateNetsuiteSettlementIngress({commit, state}, {masterLotId, envelope}) {
    return runNetsuiteIngress({
      commit,
      masterLot: requireMasterLot(state, masterLotId),
      operation: upstreamMasterLotNetsuitePrototypeAdapter.simulateNetsuiteSettlementIngress,
      input: envelope,
    })
  },

  async simulateNetsuiteInvoicePaidIngress({commit, state}, {masterLotId, event}) {
    return runNetsuiteIngress({
      commit,
      masterLot: requireMasterLot(state, masterLotId),
      operation: upstreamMasterLotNetsuitePrototypeAdapter.simulateNetsuiteInvoicePaidIngress,
      input: event,
    })
  },

  async simulateNetsuiteFileLinkIngress({commit, state}, {masterLotId, event}) {
    return runNetsuiteIngress({
      commit,
      masterLot: requireMasterLot(state, masterLotId),
      operation: upstreamMasterLotNetsuitePrototypeAdapter.simulateNetsuiteFileLinkIngress,
      input: event,
    })
  },

  async simulateNetsuiteSalesOrderSyncIngress({commit, state}, {masterLotId, event}) {
    return runNetsuiteIngress({
      commit,
      masterLot: requireMasterLot(state, masterLotId),
      operation: upstreamMasterLotNetsuitePrototypeAdapter.simulateNetsuiteSalesOrderSyncIngress,
      input: event,
    })
  },

  async uploadSettlementDoc(_ctx, {masterLotId, file, filename, contentType, byteSize, caption}) {
    const presigned = await apiPresignSettlementDoc(
      {masterLotId, filename, contentType, contentLength: byteSize},
      {file},
    )
    try {
      await uploadMasterLotSettlementDocToS3({putUrl: presigned.putUrl, file, contentType})
      const confirmed = await apiConfirmSettlementDoc({
        documentId: presigned.documentId,
        filename,
        mimeType: contentType,
        byteSize,
        caption,
      })
      return confirmed
    } catch (error) {
      // PUT or confirm failed AFTER presign parked the bytes — clean up the
      // orphan so the demo doesn't accumulate ghost blobs across retries.
      try {
        await apiDeleteSettlementDoc({masterLotId, documentId: presigned.documentId})
      } catch (_) { /* noop — best-effort cleanup */ }
      throw error
    }
  },

  async removeSettlementDoc(_ctx, {masterLotId, documentId}) {
    return apiDeleteSettlementDoc({masterLotId, documentId})
  },

  async bootstrapDemoIfEmpty({commit, state, rootGetters}) {
    if (state.demoSeeded) return Object.values(state.byId)
    if (Object.keys(state.byId).length > 0) {
      commit('SET_DEMO_SEEDED', true)
      return Object.values(state.byId)
    }
    // Seeding from scratch: torch any orphan blobs from a prior demo session
    // whose vuex state was wiped (incognito reload, localStorage cleared)
    // but whose IndexedDB still has the bytes. Mirrors resetDemoData. The
    // "already has lots" branch above explicitly skips this to avoid
    // nuking live user uploads.
    try { await clearAllSettlementDocBlobs() } catch (_) { /* noop */ }
    const fixtures = await apiSeedDemoData({netsuiteAppUrl: netsuiteAppUrlFrom(rootGetters)})
    commit('SET_BY_ID', fixtures)
    commit('SET_DEMO_SEEDED', true)
    return fixtures
  },

  async resetDemoData({commit, dispatch, rootGetters}) {
    // Wipe IndexedDB-backed settlement-doc bytes alongside the vuex state so
    // a "Reset demo data" round-trip leaves no orphan blobs from prior demo
    // sessions. Best-effort: a quota-exhausted browser shouldn't block reset.
    try { await clearAllSettlementDocBlobs() } catch (_) { /* noop */ }
    commit('RESET_STATE')
    // Reset the contract catalog in lockstep — a stakeholder hitting "Reset
    // demo data" expects a single round-trip back to the seeded baseline,
    // not a half-reset where their custom contracts persist but lots vanish.
    await dispatch('upstreamContracts/resetCatalog', null, {root: true})
    const fixtures = await apiSeedDemoData({netsuiteAppUrl: netsuiteAppUrlFrom(rootGetters)})
    commit('SET_BY_ID', fixtures)
    commit('SET_DEMO_SEEDED', true)
    return fixtures
  },

  async reconcileSourceUnits({commit, state, getters}, {
    unitIds,
    note,
    reconciledAt,
  } = {}) {
    const ids = uniqueIds(unitIds)
    if (ids.length === 0) return []
    const sourceLots = getters.getDecoratedUpstreamSourceLotsForMetals(null)
    if (!sourceLots) throw new Error('global source inventory must load before reconciliation')
    const buckets = reconciliationBuckets(sourceLots)
    const eligibleById = new Map(
      [...buckets.free, ...buckets.allocated, ...buckets.reconciled].map((unit) => [String(unit.id), unit])
    )
    for (const id of ids) {
      const unit = eligibleById.get(id)
      if (!unit) throw new Error(`source unit ${id} is not eligible for reconciliation`)
      if (unit.allocatedMasterLotId) {
        throw new Error(`source unit ${id} is allocated to ${unit.allocatedMasterLotId} and cannot be reconciled`)
      }
    }
    commit('SET_RECONCILED_SOURCE_UNITS', upsertSourceUnitReconciliations(
      state.reconciledSourceUnitsById,
      ids,
      {reconciledAt, ...(note !== undefined ? {note} : {})},
    ))
    return ids
  },

  async reconcileSourceUnitComplement({dispatch, getters}, {
    keeperIds,
    note,
    reconciledAt,
  } = {}) {
    const sourceLots = getters.getDecoratedUpstreamSourceLotsForMetals(null)
    if (!sourceLots) throw new Error('global source inventory must load before reconciliation')
    const ids = uniqueIds(keeperIds)
    const freeIds = new Set(reconciliationBuckets(sourceLots).free.map(({id}) => String(id)))
    for (const id of ids) {
      if (!freeIds.has(id)) throw new Error(`source unit ${id} is not free inventory and cannot be kept`)
    }
    const unitIds = reconciliationIdsForComplement(sourceLots, ids)
    await dispatch('reconcileSourceUnits', {unitIds, note, reconciledAt})
    return unitIds
  },

  async recoverSourceUnits({commit, state}, {unitIds} = {}) {
    const ids = uniqueIds(unitIds)
    for (const id of ids) {
      if (!state.reconciledSourceUnitsById?.[id]) {
        throw new Error(`source unit ${id} is not reconciled and cannot be recovered`)
      }
    }
    commit('SET_RECONCILED_SOURCE_UNITS', recoverSourceUnitReconciliations(
      state.reconciledSourceUnitsById,
      ids,
    ))
    return ids
  },
}

const requireMasterLot = (state, masterLotId) => {
  const masterLot = state.byId[masterLotId]
  if (!masterLot) throw new Error(`master lot not found: ${masterLotId}`)
  return masterLot
}

const runNetsuiteIngress = async ({commit, masterLot, operation, input}) => {
  const result = await operation(masterLot, input)
  return commitAdapterResult(commit, result)
}

const commitAdapterResult = (commit, {lot, changed}) => {
  if (changed) commit('UPSERT_LOT', lot)
  return lot
}

const netsuiteAppUrlFrom = (rootGetters) => {
  const configured = typeof rootGetters?.getConfig === 'function'
    ? rootGetters.getConfig('NETSUITE_APP_URL')
    : null
  return configured || DEFAULT_DEMO_NETSUITE_APP_URL
}

const mutations = {
  SET_BY_ID(state, lots) {
    const next = {}
    for (const lot of lots || []) {
      if (lot && lot.id) next[lot.id] = lot
    }
    state.byId = next
  },
  UPSERT_LOT(state, lot) {
    if (!lot || !lot.id) return
    state.byId = {...state.byId, [lot.id]: lot}
  },
  REMOVE_LOT(state, id) {
    if (!id) return
    const next = {...state.byId}
    delete next[id]
    state.byId = next
  },
  SET_UPSTREAM_SOURCE_LOTS_FOR_METALS(state, {key, lots}) {
    state.upstreamSourceLotsCacheByMetals = {
      ...state.upstreamSourceLotsCacheByMetals,
      [key]: Array.isArray(lots) ? lots : [],
    }
  },
  SET_DEMO_SEEDED(state, value) {
    state.demoSeeded = Boolean(value)
  },
  SET_RECONCILED_SOURCE_UNITS(state, registry) {
    state.reconciledSourceUnitsById = {...(registry || {})}
  },
  RESET_STATE(state) {
    Object.assign(state, getDefaultState())
  },
}

export default {
  namespaced: true,
  state: getDefaultState,
  getters,
  actions,
  mutations,
}

// --- helpers (called by getters/actions/mutations above) ---

function getDefaultState() {
  return {
    byId: {},
    upstreamSourceLotsCacheByMetals: {},
    // @mise — prototype-local shipped/inactive source-unit registry.
    reconciledSourceUnitsById: {},
    demoSeeded: false,
  }
}

// Sequence number used to derive the next user-created lot code. We scan
// existing lots for `ML-{year}-{N}` (4-digit form, ignoring the D-prefixed
// fixture form) and take max + 1, padded to 4 digits.
//
// Dual-scan rationale (keys AND lot.masterLotCode):
//   In the steady state, `state.byId` is keyed by lot id and
//   `lot.id === lot.masterLotCode`, so a key-only scan suffices. But
//   `vuex-persist` hydrates blobs serialized by older codebases — at least
//   one historical revision keyed lots by a UUID-shaped id while the inner
//   `masterLotCode` already used the canonical ML-{year}-#### form. A
//   key-only scan misses those lots and emits a colliding code on the next
//   createMasterLot (manifesting as duplicate `ML-2026-0001` rows in the
//   listing). Scanning both surfaces makes us collision-safe against any
//   such drift without needing a one-shot data migration.
function computeNextUserLotCode(masterLots) {
  const year = new Date().getFullYear()
  const userPattern = new RegExp(`^ML-${year}-(\\d{4,})$`)
  const candidateCodes = new Set()
  for (const key of Object.keys(masterLots || {})) candidateCodes.add(key)
  for (const lot of Object.values(masterLots || {})) {
    if (lot && lot.masterLotCode) candidateCodes.add(lot.masterLotCode)
  }
  const sequenceNumbers = [...candidateCodes]
    .map((code) => {
      const match = code.match(userPattern)
      return match ? parseInt(match[1], 10) : 0
    })
    .filter((sequenceNumber) => Number.isFinite(sequenceNumber))
  const nextSequenceNumber = (sequenceNumbers.length ? Math.max(...sequenceNumbers) : 0) + 1
  return `ML-${year}-${String(nextSequenceNumber).padStart(4, '0')}`
}

function upstreamSourceLotsCacheKey(paidMetals) {
  if (!Array.isArray(paidMetals) || paidMetals.length === 0) return '__none__'
  return [...paidMetals].map((metal) => String(metal).toLowerCase()).sort().join(',')
}

function mergeUpstreamSourceLotsWithFixtures(liveLots, fixtureLots) {
  const liveRows = Array.isArray(liveLots) ? liveLots : []
  const fixtures = Array.isArray(fixtureLots) ? fixtureLots : []
  const liveLotIds = new Set(liveRows.map((lot) => lot?.id).filter(Boolean))
  const liveUnitIds = new Set(liveRows.flatMap((lot) =>
    Array.isArray(lot?.units) ? lot.units.map((unit) => unit?.id).filter(Boolean) : []
  ))

  // Live inventory first; fixtures append after (chef-ratified merge order).
  // Live lot/unit ids win — colliding fixture lots are skipped, colliding
  // fixture units are stripped from otherwise-new fixture lots.
  const appendedFixtures = []
  for (const lot of fixtures) {
    if (!lot?.id || liveLotIds.has(lot.id)) continue
    const units = Array.isArray(lot.units)
      ? lot.units.filter((unit) => unit?.id && !liveUnitIds.has(unit.id))
      : []
    if (units.length === 0) continue
    appendedFixtures.push({...lot, units})
  }

  return [...liveRows, ...appendedFixtures]
}

function uniqueIds(unitIds) {
  return [...new Set((Array.isArray(unitIds) ? unitIds : []).filter(Boolean).map(String))]
}

export const __testing__ = {
  computeNextUserLotCode,
  upstreamSourceLotsCacheKey,
  getDefaultState,
  mergeUpstreamSourceLotsWithFixtures,
}
